import {
  IconButton,
  observeIntersection,
  observeResize,
} from '@affine/component';
import type { DocCommentEntity } from '@affine/core/modules/comment/entities/doc-comment';
import { CommentPanelService } from '@affine/core/modules/comment/services/comment-panel-service';
import { DocCommentManagerService } from '@affine/core/modules/comment/services/doc-comment-manager';
import type { DocComment } from '@affine/core/modules/comment/types';
import {
  type PDF,
  type PDFPage,
  type PDFPageTextContent,
  PDFService,
  PDFStatus,
} from '@affine/core/modules/pdf';
import type { PDFMeta } from '@affine/core/modules/pdf/renderer';
import type { PageSize } from '@affine/core/modules/pdf/renderer/types';
import {
  PdfPageBitmapSurface,
  PDFPageCanvas,
} from '@affine/core/modules/pdf/views';
import {
  readPdfPageBitmapCache,
  writePdfPageBitmapCache,
} from '@affine/core/modules/pdf/views/page-bitmap-cache';
import { PDFHighlightOverlay } from '@affine/core/modules/pdf/views/page-renderer';
import type { PDFPageHighlightRect } from '@affine/core/modules/pdf/views/pdf-highlight-overlay';
import { SURFACE_DELIVERED_CONTENT_ATTRIBUTE } from '@affine/core/modules/streamlain/copilot/context-picking-attributes';
import { FullscreenRegistry } from '@affine/core/modules/streamlain/services/fullscreen-registry';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { stopPropagation } from '@affine/core/utils';
import { resolveBlobDisplayName } from '@affine/core/utils/blob-display-name';
import type { AttachmentPdfSignaturePlacement } from '@blocksuite/affine/model';
import {
  ArrowDownSmallIcon,
  ArrowUpSmallIcon,
  AttachmentIcon,
  ExpandFullIcon,
} from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type MouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useSignedPdfVersionForBlob,
  useWorkspacePdfSignatures,
} from '../../../desktop/dialogs/setting/workspace-setting/signatures/use-workspace-pdf-signatures';
import type { AttachmentViewerProps } from '../types';
import { createPdfBlockIndexReviewLocator } from './pdf-index-review';
import {
  PdfSearchCoverageBadge,
  PdfSearchCoverageCoachmark,
} from './pdf-search-coverage-badge';
import { useStoredPdfSignaturePlacements } from './pdf-signature-placements';
import { PdfSignedBadge } from './pdf-signed-badge';
import {
  getPdfCommentHighlightRects,
  useStoredPdfTextHighlights,
} from './pdf-text-highlights';
import {
  clampPageIndex,
  createPdfCanvasPresentationState,
  updateAttachmentPdfCanvasPresentation,
  useAttachmentPdfCanvasPresentation,
} from './pdf-viewer-state';
import { PdfVisualSignatureOverlay } from './pdf-visual-signature-overlay';
import * as styles from './styles.css';
import { usePdfSearchCoverage } from './use-pdf-search-coverage';

const EMBEDDED_PDF_VIEWER_PADDING = 0;
const EMBEDDED_PDF_RENDER_OVERSAMPLE = 2.5;
const MAX_EMBEDDED_PDF_RENDER_SCALE = 6;

function defaultMeta() {
  return {
    pageCount: 0,
    pageSizes: [],
    maxSize: { width: 0, height: 0 },
  };
}

function clampEmbeddedRenderScale(scale: number) {
  return Math.min(
    MAX_EMBEDDED_PDF_RENDER_SCALE,
    Math.max(window.devicePixelRatio, scale)
  );
}

function getEmbeddedViewportInfo(viewer: HTMLElement) {
  return {
    width: viewer.clientWidth,
    height: viewer.clientHeight,
  };
}

function areEmbeddedViewportInfosEqual(
  a: { width: number; height: number },
  b: { width: number; height: number }
) {
  return Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1;
}

function getEmbeddedPageDisplayState(
  pageSize: PageSize | null,
  viewportInfo: { width: number; height: number }
) {
  if (!pageSize || viewportInfo.width <= 0 || viewportInfo.height <= 0) {
    return null;
  }

  const availableWidth = Math.max(
    1,
    viewportInfo.width - EMBEDDED_PDF_VIEWER_PADDING
  );
  const availableHeight = Math.max(
    1,
    viewportInfo.height - EMBEDDED_PDF_VIEWER_PADDING
  );
  const displayScale = Math.min(
    availableWidth / pageSize.width,
    availableHeight / pageSize.height
  );
  const width = pageSize.width * displayScale;
  const height = pageSize.height * displayScale;
  const renderScale = clampEmbeddedRenderScale(
    displayScale * window.devicePixelRatio * EMBEDDED_PDF_RENDER_OVERSAMPLE
  );

  return { width, height, renderScale };
}

type PdfPreviewNavigation = {
  pageIndex: number;
  requestId: string;
};

function usePdfPreviewNavigation(
  viewerRef: RefObject<HTMLDivElement | null>,
  blockId: string
) {
  const [navigation, setNavigation] = useState<PdfPreviewNavigation | null>(
    null
  );

  useEffect(() => {
    const viewer = viewerRef.current;
    const previewEditor = viewer?.closest<HTMLElement>(
      '[data-streamlain-peek-preview="true"]'
    );
    if (!previewEditor) {
      setNavigation(null);
      return;
    }

    const readNavigation = () => {
      const targetBlockId = previewEditor.getAttribute(
        'data-streamlain-pdf-preview-block-id'
      );
      const rawPageIndex = previewEditor.getAttribute(
        'data-streamlain-pdf-preview-page-index'
      );
      if (targetBlockId !== blockId || !rawPageIndex) {
        setNavigation(null);
        return;
      }
      const pageIndex = Number.parseInt(rawPageIndex, 10);
      if (!Number.isInteger(pageIndex) || pageIndex < 0) {
        setNavigation(null);
        return;
      }
      setNavigation({
        pageIndex,
        requestId:
          previewEditor.getAttribute(
            'data-streamlain-pdf-preview-request-id'
          ) ?? '',
      });
    };

    readNavigation();
    const observer = new MutationObserver(readNavigation);
    observer.observe(previewEditor, {
      attributeFilter: [
        'data-streamlain-pdf-preview-block-id',
        'data-streamlain-pdf-preview-page-index',
        'data-streamlain-pdf-preview-request-id',
      ],
      attributes: true,
    });
    return () => observer.disconnect();
  }, [blockId, viewerRef]);

  return navigation;
}

export function PDFViewerEmbedded({ model }: AttachmentViewerProps) {
  const scale = window.devicePixelRatio;
  const fullscreenRegistry = useService(FullscreenRegistry);
  const pdfService = useService(PDFService);
  const commentManager = useService(DocCommentManagerService);
  const commentPanelService = useService(CommentPanelService);
  const [pdfEntity, setPdfEntity] = useState<{
    pdf: PDF;
    release: () => void;
  } | null>(null);
  const [pageEntity, setPageEntity] = useState<{
    page: PDFPage;
    release: () => void;
  } | null>(null);

  const meta = useLiveData(
    useMemo(() => {
      return pdfEntity
        ? pdfEntity.pdf.state$.map(s => {
            return s.status === PDFStatus.Opened ? s.meta : defaultMeta();
          })
        : new LiveData<PDFMeta>(defaultMeta());
    }, [pdfEntity])
  );
  const img = useLiveData(
    useMemo(() => (pageEntity ? pageEntity.page.bitmap$ : null), [pageEntity])
  );
  const text = useLiveData(
    useMemo(() => (pageEntity ? pageEntity.page.text$ : null), [pageEntity])
  );

  const name = useLiveData(
    useMemo(() => LiveData.fromSignal(model.props.name$), [model])
  );
  const blobId = useLiveData(
    useMemo(() => LiveData.fromSignal(model.props.sourceId$), [model])
  );
  const signaturePlacements = useStoredPdfSignaturePlacements(model);
  const signatures = useWorkspacePdfSignatures(blobId, false);
  const relevantSignaturePlacements = useMemo(
    () =>
      signaturePlacements.filter(
        placement => placement.sourceId === (blobId ?? '')
      ),
    [blobId, signaturePlacements]
  );
  const displayName = resolveBlobDisplayName({
    blobId,
    occurrenceName: name,
    mimeType: model.props.type,
  });
  const signedVersionState = useSignedPdfVersionForBlob(
    blobId,
    Boolean(blobId)
  );
  const signedVersion = signedVersionState.signedVersion;
  const [cursor, setCursor] = useState(0);
  const [visibility, setVisibility] = useState(false);
  const [viewportInfo, setViewportInfo] = useState({
    width: 0,
    height: 0,
  });
  const viewerRef = useRef<HTMLDivElement>(null);
  const previewNavigation = usePdfPreviewNavigation(viewerRef, model.id);
  const scrollHostRef = useRef<HTMLElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorRef = useRef(cursor);
  const localPresentationUpdatedAtRef = useRef<number | null>(null);
  const applyingPresentationRef = useRef(false);
  const initialPresentationAppliedRef = useRef(false);
  const presentationSaveTimerRef = useRef<number | null>(null);
  const coverage = usePdfSearchCoverage({ model });
  const workspace = useService(WorkspaceService).workspace;
  const [commentEntity, setCommentEntity] = useState<DocCommentEntity | null>(
    null
  );
  const comments = useLiveData(
    commentEntity?.comments$ ?? new LiveData<DocComment[]>([])
  );
  const highlightedCommentId = useLiveData(
    commentEntity?.commentHighlighted$ ?? new LiveData<string | null>(null)
  );
  const storedHighlights = useStoredPdfTextHighlights(model);
  const canvasPresentation = useAttachmentPdfCanvasPresentation(model);

  const pdfHighlightRects = useMemo(
    () =>
      getPdfCommentHighlightRects({
        comments,
        storedHighlights,
        blockId: model.id,
        sourceId: blobId ?? '',
        pageIndex: cursor,
        activeCommentId: highlightedCommentId,
      }),
    [blobId, comments, cursor, highlightedCommentId, model.id, storedHighlights]
  );
  const currentPageSize = meta.pageSizes[cursor] ?? null;
  const embeddedPageDisplayState = useMemo(
    () => getEmbeddedPageDisplayState(currentPageSize, viewportInfo),
    [currentPageSize, viewportInfo]
  );

  // Fresh render wins. Otherwise show the cached bitmap from a previous mount
  // (canvas culling remounts this component constantly) — but only once the
  // display size is known, because the cached bitmap's resolution carries an
  // oversample factor and must not be shown at its intrinsic size.
  const displayImg =
    img ??
    (blobId && currentPageSize && embeddedPageDisplayState
      ? readPdfPageBitmapCache(
          blobId,
          cursor,
          'embedded',
          currentPageSize.width * embeddedPageDisplayState.renderScale
        )
      : null);

  useEffect(() => {
    if (!img || !blobId) return;
    writePdfPageBitmapCache(blobId, cursor, 'embedded', img);
  }, [blobId, cursor, img]);

  const stopPdfTextInteractionWhenFocused = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('[data-pdf-text-layer="true"]')) return;
    if (!viewerRef.current?.closest('.affine-attachment-container.focused')) {
      return;
    }

    event.stopPropagation();
  }, []);

  const openAttachment = useCallback(() => {
    fullscreenRegistry.open('attachment', model.id);
  }, [fullscreenRegistry, model.id]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  const persistEmbeddedPresentation = useCallback(
    (pageIndex = cursorRef.current) => {
      if (previewNavigation) return;
      if (meta.pageCount <= 0) return;
      if (!initialPresentationAppliedRef.current) return;
      const host = scrollHostRef.current;
      const state = createPdfCanvasPresentationState({
        pageIndex: clampPageIndex(pageIndex, meta.pageCount - 1),
        scrollLeft: host?.scrollLeft ?? 0,
        scrollTop: host?.scrollTop ?? 0,
      });
      localPresentationUpdatedAtRef.current = state.updatedAt;
      updateAttachmentPdfCanvasPresentation({ model, state });
    },
    [meta.pageCount, model, previewNavigation]
  );

  const scheduleEmbeddedPresentationPersist = useCallback(
    (pageIndex = cursorRef.current) => {
      if (applyingPresentationRef.current) return;
      if (!initialPresentationAppliedRef.current) return;
      if (presentationSaveTimerRef.current !== null) {
        window.clearTimeout(presentationSaveTimerRef.current);
      }
      presentationSaveTimerRef.current = window.setTimeout(() => {
        presentationSaveTimerRef.current = null;
        persistEmbeddedPresentation(pageIndex);
      }, 180);
    },
    [persistEmbeddedPresentation]
  );

  const onOpenFullscreenDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button, [role="menu"], input, textarea, select')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      openAttachment();
    },
    [openAttachment]
  );

  const onPdfHighlightClick = useCallback(
    (rect: PDFPageHighlightRect) => {
      if (!rect.commentId) return;
      commentPanelService.openCommentPanel();
      commentEntity?.highlightComment(rect.commentId);
    },
    [commentEntity, commentPanelService]
  );

  const navigator = useMemo(() => {
    const p = cursor - 1;
    const n = cursor + 1;

    return {
      prev: {
        disabled: p < 0,
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          setCursor(p);
          scheduleEmbeddedPresentationPersist(p);
        },
      },
      next: {
        disabled: n >= meta.pageCount,
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          setCursor(n);
          scheduleEmbeddedPresentationPersist(n);
        },
      },
      peek: {
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          openAttachment();
        },
      },
    };
  }, [cursor, meta, openAttachment, scheduleEmbeddedPresentationPersist]);

  const indexReviewLocator = useMemo(
    () =>
      createPdfBlockIndexReviewLocator({
        workspaceId: workspace.id,
        docId: model.store.id,
        blockId: model.id,
        blobId,
        pageIndex: cursor,
      }),
    [blobId, cursor, model.id, model.store.id, workspace.id]
  );

  const selectReviewPage = useCallback(
    (pageIndex: number) => {
      const target = clampPageIndex(pageIndex, Math.max(0, meta.pageCount - 1));
      setCursor(target);
      scheduleEmbeddedPresentationPersist(target);
    },
    [meta.pageCount, scheduleEmbeddedPresentationPersist]
  );

  useEffect(() => {
    return () => {
      if (presentationSaveTimerRef.current !== null) {
        window.clearTimeout(presentationSaveTimerRef.current);
      }
      persistEmbeddedPresentation();
    };
  }, [persistEmbeddedPresentation]);

  useEffect(() => {
    scheduleEmbeddedPresentationPersist(cursor);
  }, [cursor, scheduleEmbeddedPresentationPersist]);

  useEffect(() => {
    if (!previewNavigation || meta.pageCount <= 0) return;
    initialPresentationAppliedRef.current = true;
    applyingPresentationRef.current = true;
    setCursor(clampPageIndex(previewNavigation.pageIndex, meta.pageCount - 1));
    const frame = window.requestAnimationFrame(() => {
      const host = scrollHostRef.current;
      if (host) {
        host.scrollLeft = 0;
        host.scrollTop = 0;
      }
      applyingPresentationRef.current = false;
    });
    return () => {
      window.cancelAnimationFrame(frame);
      applyingPresentationRef.current = false;
    };
  }, [meta.pageCount, previewNavigation]);

  useEffect(() => {
    if (meta.pageCount <= 0) return;
    if (previewNavigation) return;
    if (!canvasPresentation) {
      initialPresentationAppliedRef.current = true;
      return;
    }
    if (
      canvasPresentation.updatedAt === localPresentationUpdatedAtRef.current
    ) {
      initialPresentationAppliedRef.current = true;
      return;
    }

    applyingPresentationRef.current = true;
    const nextPageIndex = clampPageIndex(
      canvasPresentation.pageIndex,
      meta.pageCount - 1
    );
    setCursor(nextPageIndex);
    const frame = window.requestAnimationFrame(() => {
      const host = scrollHostRef.current;
      if (host) {
        host.scrollLeft = canvasPresentation.scrollLeft;
        host.scrollTop = canvasPresentation.scrollTop;
      }
      applyingPresentationRef.current = false;
      initialPresentationAppliedRef.current = true;
    });

    return () => {
      window.cancelAnimationFrame(frame);
      applyingPresentationRef.current = false;
    };
  }, [canvasPresentation, meta.pageCount, previewNavigation]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!displayImg) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = displayImg.width;
    canvas.height = displayImg.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(displayImg, 0, 0);
  }, [displayImg]);

  useEffect(() => {
    if (!visibility) return;
    if (!pageEntity) return;
    if (!currentPageSize) return;

    const { width, height } = currentPageSize;
    const renderScale = embeddedPageDisplayState?.renderScale ?? scale;

    pageEntity.page.render({ width, height, scale: renderScale });
    pageEntity.page.loadText();

    return () => {
      // reset, not unsubscribe: unsubscribe kills the effect pipeline for good
      // (the pooled PDFPage entity outlives this hook by up to the pool GC
      // window), so a re-run after a size/scale change would render nothing.
      pageEntity.page.render.reset();
      pageEntity.page.loadText.reset();
    };
  }, [
    visibility,
    pageEntity,
    currentPageSize,
    embeddedPageDisplayState,
    scale,
  ]);

  useEffect(() => {
    if (!visibility) return;
    if (!pdfEntity) return;

    const size = meta.pageSizes[cursor];
    if (!size) return;

    const { width, height } = size;
    const renderScale = embeddedPageDisplayState?.renderScale ?? scale;
    const pageEntity = pdfEntity.pdf.page(
      cursor,
      `${width}:${height}:${renderScale}`
    );

    setPageEntity(pageEntity);

    return () => {
      pageEntity.release();
      setPageEntity(null);
    };
  }, [visibility, pdfEntity, cursor, meta, embeddedPageDisplayState, scale]);

  useEffect(() => {
    if (!visibility) return;
    if (!blobId) return;

    const pdfEntity = pdfService.get(blobId);

    setPdfEntity(pdfEntity);

    return () => {
      pdfEntity.release();
      setPdfEntity(null);
    };
  }, [blobId, pdfService, visibility]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    // Asymmetric visibility: show IMMEDIATELY when intersecting (a symmetric
    // debounce here delayed the preview by 377ms on every pan-back, while
    // image blocks appear instantly), hide only after a grace period so brief
    // pan-throughs don't release the PDF entity.
    let hideTimer: number | null = null;
    const unobserve = observeIntersection(viewer, entry => {
      if (entry.isIntersecting) {
        if (hideTimer !== null) {
          window.clearTimeout(hideTimer);
          hideTimer = null;
        }
        setVisibility(true);
        return;
      }
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
      }
      hideTimer = window.setTimeout(() => {
        hideTimer = null;
        setVisibility(false);
      }, 377);
    });

    return () => {
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
      }
      unobserve();
    };
  }, []);

  useEffect(() => {
    const scrollHost = scrollHostRef.current;
    if (!scrollHost) return;

    setViewportInfo(current => {
      const next = getEmbeddedViewportInfo(scrollHost);
      return areEmbeddedViewportInfosEqual(current, next) ? current : next;
    });

    return observeResize(scrollHost, ({ contentRect: { width, height } }) => {
      const next = {
        width,
        height,
      };
      setViewportInfo(current =>
        areEmbeddedViewportInfosEqual(current, next) ? current : next
      );
    });
  }, []);

  useEffect(() => {
    const entityRef = commentManager.get(model.store.id);
    setCommentEntity(entityRef.obj);
    entityRef.obj.revalidate();
    return () => {
      entityRef.release();
      setCommentEntity(null);
    };
  }, [commentManager, model.store.id]);

  return (
    <div
      ref={viewerRef}
      className={styles.pdfContainer}
      data-colour-island="document"
      onClick={coverage.closeContextMenu}
      onDoubleClick={onOpenFullscreenDoubleClick}
      onMouseDownCapture={stopPdfTextInteractionWhenFocused}
      onDoubleClickCapture={stopPdfTextInteractionWhenFocused}
    >
      <main
        ref={scrollHostRef}
        className={clsx(styles.pdfViewer, styles.pdfEmbeddedViewer)}
        onScroll={() => scheduleEmbeddedPresentationPersist()}
      >
        <div
          className={clsx(styles.pdfPage, styles.pdfEmbeddedPageFrame)}
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            minHeight: '100%',
          }}
        >
          <PdfEmbeddedPage
            displaySize={embeddedPageDisplayState}
            img={displayImg}
            scale={scale}
            text={text}
            highlightRects={pdfHighlightRects}
            onHighlightClick={onPdfHighlightClick}
            pageIndex={cursor}
            canvasRef={canvasRef}
            signaturePlacements={relevantSignaturePlacements}
            signatureAppearanceImageUrl={signatures.appearanceImageUrl}
          />
        </div>

        <div className={styles.pdfControls}>
          <IconButton
            size={16}
            icon={<ArrowUpSmallIcon />}
            className={styles.pdfControlButton}
            onDoubleClick={stopPropagation}
            aria-label="Prev"
            {...navigator.prev}
          />
          <IconButton
            size={16}
            icon={<ArrowDownSmallIcon />}
            className={styles.pdfControlButton}
            onDoubleClick={stopPropagation}
            aria-label="Next"
            {...navigator.next}
          />
          <IconButton
            size={16}
            icon={<ExpandFullIcon />}
            className={styles.pdfControlButton}
            onDoubleClick={stopPropagation}
            {...navigator.peek}
          />
        </div>
        <PdfSearchCoverageCoachmark
          badgeState={coverage.coverageState}
          dismissed={coverage.coachmarkDismissed}
          hidden={coverage.isHidden}
          onDismissPermanently={coverage.dismissCoachmarkPermanently}
          open={coverage.showCoverageCoachmark}
        />
        {signedVersion ? <PdfSignedBadge version={signedVersion} /> : null}
      </main>
      <footer className={styles.pdfFooter}>
        <div className={clsx([styles.pdfFooterItem, { truncate: true }])}>
          <AttachmentIcon />
          <span className={clsx([styles.pdfTitle, 'pdf-name'])}>
            {displayName}
          </span>
        </div>
        <div className={styles.pdfFooterMeta}>
          <PdfSearchCoverageBadge
            state={coverage.coverageState}
            hidden={coverage.isHidden}
            disabled={coverage.isDisabled}
            className={styles.pdfEmbeddingBadgeHostFooter}
            pageReview={{
              locator: indexReviewLocator,
              currentPageIndex: cursor,
              pageCount: meta.pageCount,
              onPageSelect: selectReviewPage,
            }}
            onIndexText={
              coverage.canIndexPdfText
                ? () => {
                    void coverage.indexPdfText();
                  }
                : undefined
            }
            onScanImageText={() => {
              void coverage.scanImageText();
            }}
            onCheckAgain={() => {
              coverage.retryCoverageCheck();
            }}
          />
          <div className={clsx([styles.pdfFooterItem, styles.pdfPageCount])}>
            <span className="page-cursor">
              {meta.pageCount > 0 ? cursor + 1 : '-'}
            </span>
            /
            <span className="page-count">
              {meta.pageCount > 0 ? meta.pageCount : '-'}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PdfEmbeddedPage({
  canvasRef,
  displaySize,
  highlightRects,
  img,
  onHighlightClick,
  pageIndex,
  scale,
  text,
  signaturePlacements,
  signatureAppearanceImageUrl,
}: {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  displaySize: { width: number; height: number } | null;
  highlightRects: PDFPageHighlightRect[];
  img: ImageBitmap | null;
  onHighlightClick?: (
    rect: PDFPageHighlightRect,
    position: { left: number; top: number }
  ) => void;
  pageIndex: number;
  scale: number;
  text: PDFPageTextContent | null;
  signaturePlacements: AttachmentPdfSignaturePlacement[];
  signatureAppearanceImageUrl: (appearanceId: string) => string;
}) {
  if (!img) {
    return (
      <PdfPageBitmapSurface
        bitmap={null}
        displayWidth={displaySize?.width ?? null}
        displayHeight={displaySize?.height ?? null}
        data-pdf-embedded-cache="false"
      />
    );
  }

  const width = img.width / scale;
  const height = img.height / scale;
  const displayWidth = displaySize?.width ?? width;
  const displayHeight = displaySize?.height ?? height;

  return (
    <div
      className={styles.pdfEmbeddedPageLayer}
      data-pdf-page-index={pageIndex}
      style={{
        width: displayWidth,
        height: displayHeight,
        aspectRatio: `${displayWidth} / ${displayHeight}`,
      }}
    >
      <PDFPageCanvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
      <PDFHighlightOverlay
        rects={highlightRects}
        onHighlightClick={onHighlightClick}
      />
      <PdfVisualSignatureOverlay
        placements={signaturePlacements}
        pageIndex={pageIndex}
        appearanceImageUrl={signatureAppearanceImageUrl}
      />
      {text && text.glyphs.length > 0 ? (
        <div
          className={styles.pdfEmbeddedTextLayer}
          data-pdf-text-layer="true"
          {...{ [SURFACE_DELIVERED_CONTENT_ATTRIBUTE]: '' }}
        >
          {text.glyphs.map((glyph, index) => (
            <span
              key={`${index}:${glyph.text}:${glyph.left}:${glyph.top}`}
              className={styles.pdfEmbeddedTextGlyph}
              style={{
                left: `${glyph.left * 100}%`,
                top: `${glyph.top * 100}%`,
                width: `${glyph.width * 100}%`,
                height: `${glyph.height * 100}%`,
                fontSize: `${Math.max(1, glyph.height * displayHeight)}px`,
              }}
            >
              {glyph.text}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
